import {
  QUESTIONNAIRE_NEW,
  QUESTIONNAIRE_DIRTY_RESET,
  QUESTIONNAIRE_CLEAR,
  BLOCK_ANSWERED,
  QUESTION_ANSWERED,
  VALIDATE_QUESTIONNAIRE_BLOCKS
} from 'actions';

import { createFormQuestionnaire, createPreQualQuestionnaire } from 'common/questionnaires/creater';
import { validateQuestion } from 'components/Questionnaires/validators';
import QuestionnaireType from 'common/questionnaires/QuestionnaireType';

const isVisible = (q, blockAnswers, formAnswers, preQualAnswers) => {
  if (typeof q.visible === 'boolean') {
    return q.visible;
  }
  if (typeof q.visible === 'function') {
    return q.visible(blockAnswers, formAnswers, preQualAnswers);
  }
  return false;
};

const isBlockVisible = (b, formAnswers, preQualAnswers) => {
  if (b.linkBlock === true && !b.answers) {
    return false;
  }
  if (typeof b.visible === 'boolean') {
    return b.visible;
  }
  if (typeof b.visible === 'function') {
    return b.visible(formAnswers, preQualAnswers);
  }
  return false;
};

const isEmptyObject = (obj) => {
  if (!obj) {
    return true;
  }
  let isEmpty = true;
  Object.keys(obj).forEach((key) => {
    if (obj[key]) {
      isEmpty = false;
    }
  });
  return isEmpty;
};

// eslint-disable-next-line default-param-last
function questionnaires(state = {
  preQualQuestionnaire: { answers: { } },
  formQuestionnaire: null,
  isValidating: false,
  validationResult: null
}, action) {
  switch (action.type) {
    case QUESTIONNAIRE_NEW: {
      const createPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      const questionnaire = createPreQual ? createPreQualQuestionnaire(action.rcEnabled) : createFormQuestionnaire(action.formType, action.selectedAccountId, action.customerDetails, action.lockSettings);
      return {
        ...state,
        preQualQuestionnaire: createPreQual ? questionnaire : state.preQualQuestionnaire,
        formQuestionnaire: !createPreQual ? questionnaire : state.formQuestionnaire,
      };
    }
    case QUESTIONNAIRE_DIRTY_RESET: {
      let updated = {};
      const isPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      if (isPreQual) {
        updated = { ...state.preQualQuestionnaire, dirty: false };
      } else {
        updated = { ...state.formQuestionnaire, dirty: false };
      }
      return {
        ...state,
        preQualQuestionnaire: isPreQual ? updated : state.preQualQuestionnaire,
        formQuestionnaire: !isPreQual ? updated : state.formQuestionnaire,
      };
    }
    case QUESTIONNAIRE_CLEAR: {
      const isPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      return {
        ...state,
        preQualQuestionnaire: isPreQual ? { answers: {} } : state.preQualQuestionnaire,
        formQuestionnaire: !isPreQual ? null : state.formQuestionnaire,
      };
    }
    case BLOCK_ANSWERED: {
      let updated = {};
      const isPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      if (isPreQual) {
        updated = { ...state.preQualQuestionnaire, dirty: true };
      } else {
        updated = { ...state.formQuestionnaire, dirty: true };
      }

      if (!updated.blocks) {
        return { ...state };
      }

      if (!updated.answers) {
        updated.answers = {};
      }

      if (updated.answers && !Object.getOwnPropertyDescriptor.hasOwnProperty.call(updated.answers, action.blockId)) {
        updated.answers[action.blockId] = {};
      }
      updated.answers[action.blockId] = action.answer;

      const updatedBlocks = updated.blocks.map((b, i) => {
        if (b.id === action.blockId) {
          if (!Object.getOwnPropertyDescriptor.hasOwnProperty.call(b, 'answers')) {
            b = { ...b, answers: {} };
          } else {
            b = { ...b };
          }
          b.answers = action.answer;
          b.isValid = action.isValid;
        } else if (!isPreQual && !isEmptyObject(b.answers) && !isBlockVisible(b, updated?.answers, state.preQualQuestionnaire?.answers)) {
          b = Object.assign({}, b);
          b.answers = {};
          b.isValid = undefined;
          if (b.questions?.length > 0) {
            b.questions = b.questions.map((q) => {
              q.isValid = undefined;
              q.errorMessages = undefined;
              q.answer = undefined;

              return q;
            });
          }
          updated.answers[b.id] = {};
        } else if (b.questions?.length > 0) {
          b.questions = b.questions.map((q) => {
            const questionIndex = b.questions.findIndex(x => x.id === action.questionId);
            if (isPreQual && questionIndex !== -1 && i > questionIndex) {
              // clear Pre-Qual Questionnaire answers after answered question
              q.isValid = undefined;
              q.errorMessages = undefined;
              q.answer = undefined;
              if (updated.answers && updated.answers[b.id]) {
                updated.answers[b.id][q.id] = undefined;
              }
              if (b.answers && b.answers[b.id]) {
                b.answers[q.id] = undefined;
              }
            } else if (!isPreQual && !isVisible(q, b.answers, updated?.answers, state.preQualQuestionnaire?.answers)) {
              q.isValid = undefined;
              q.errorMessages = undefined;
              q.answer = undefined;
              if (updated.answers && updated.answers[b.id]) {
                updated.answers[b.id][q.id] = undefined;
              }
              if (b.answers && b.answers[q.id]) {
                b.answers[q.id] = undefined;
              }
            }
            return q;
          });
          b.isValid = b.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
        }
        b.isForceEdited = undefined;
        return b;
      });

      updated = { ...updated, blocks: updatedBlocks };

      return {
        ...state,
        formQuestionnaire: updated,
      };
    }
    case QUESTION_ANSWERED: {
      let updated = {};
      const isPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      if (isPreQual) {
        updated = { ...state.preQualQuestionnaire, dirty: true };
      } else {
        updated = { ...state.formQuestionnaire, dirty: true };
      }

      if (!updated.blocks) {
        return { ...state };
      }

      if (!updated.answers) {
        updated.answers = {};
      }

      if (updated.answers && !Object.getOwnPropertyDescriptor.hasOwnProperty.call(updated.answers, action.blockId)) {
        updated.answers[action.blockId] = {};
      }

      updated.answers[action.blockId][action.questionId] = action.answer;

      const blockIndex = updated.blocks.findIndex(b => b.id === action.blockId);

      const updatedBlocks = updated.blocks.map((b, i) => {
        if (b.id === action.blockId) {
          if (!Object.getOwnPropertyDescriptor.hasOwnProperty.call(b, 'answers')) {
            b = { ...b, answers: {} };
          } else {
            b = { ...b };
          }
          b.answers[action.questionId] = action.answer;
          const questionIndex = b.questions.findIndex(q => q.id === action.questionId);
          b.questions = b.questions.map((q) => {
            if (q.id === action.questionId) {
              q = { ...q, answer: action.answer };
              validateQuestion(q);
              q.isForceEdited = undefined;
            } else if (isPreQual && questionIndex !== -1 && i > questionIndex) {
              // clear Pre-Qual Questionnaire answers after answered question
              q.isValid = undefined;
              q.errorMessages = undefined;
              q.answer = undefined;
              if (updated.answers && updated.answers[b.id]) {
                updated.answers[b.id][q.id] = undefined;
              }
              if (b.answers && b.answers[b.id]) {
                b.answers[q.id] = undefined;
              }
            } else if (!isPreQual && !isVisible(q, b.answers, updated?.answers, state.preQualQuestionnaire?.answers)) {
              q.isValid = undefined;
              q.errorMessages = undefined;
              q.answer = undefined;
              if (updated.answers && updated.answers[b.id]) {
                updated.answers[b.id][q.id] = undefined;
              }
              if (b.answers && b.answers[q.id]) {
                b.answers[q.id] = undefined;
              }
            }
            return q;
          });
          b.isValid = b.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
          b.isForceEdited = undefined;
        } else if (isPreQual && blockIndex !== -1 && i > blockIndex) {
          // clear Pre-Qual Questionnaire answers after answered block
          const blockQuestions = b.questions.map(q => Object.assign(q, { isValid: undefined, errorMessages: undefined, answer: undefined }));
          if (updated.answers) {
            updated.answers[b.id] = {};
          }
          b = Object.assign({}, b);
          b.questions = [...blockQuestions];
          b.isValid = undefined;
          b.answers = {};
        } else {
          // Clear any isForceEdited flags
          b.isForceEdited = undefined;
          if (b.questions?.length > 0) {
            const bQuestions = b.questions.map((q) => {
              const qChanges = {};
              let hasChanged = false;
              if (q.isForceEdited !== undefined) {
                qChanges.isForceEdited = undefined;
                hasChanged = true;
              }
              if (!isPreQual && q.answer && !isVisible(q, b.answers, updated?.answers, state.preQualQuestionnaire?.answers)) {
                qChanges.isValid = undefined;
                qChanges.errorMessages = undefined;
                qChanges.answer = undefined;
                b.answers[q.id] = undefined;
                updated.answers[b.id][q.id] = undefined;
                hasChanged = true;
              }

              return hasChanged ? Object.assign(q, qChanges) : q;
            });
            b.questions = bQuestions;
          } else if (!isPreQual && !isEmptyObject(b.answers) && !isBlockVisible(b, updated?.answers, state.preQualQuestionnaire?.answers)) {
            b = Object.assign({}, b);
            b.answers = {};
            updated.answers[b.id] = undefined;
          }
        }

        return b;
      });

      updated = { ...updated, blocks: [...updatedBlocks] };

      return {
        ...state,
        preQualQuestionnaire: isPreQual ? updated : state.preQualQuestionnaire,
        formQuestionnaire: !isPreQual ? updated : state.formQuestionnaire,
      };
    }
    case VALIDATE_QUESTIONNAIRE_BLOCKS: {
      let updated = {};
      const isPreQual = action.questionnaireType === QuestionnaireType.PreQualifying;
      if (isPreQual) {
        updated = { ...state.preQualQuestionnaire, dirty: true };
      } else {
        updated = { ...state.formQuestionnaire, dirty: true };
      }

      const updatedBlocks = updated.blocks.map((b, i) => {
        let blockValid = b?.isValid ?? true;
        if (b?.questions?.length > 0) {
          b.questions = b.questions.map((q) => {
            let questionVisible = false;
            if (Object.prototype.hasOwnProperty.call(q, 'visible')) {
              if (typeof q.visible === 'boolean') {
                questionVisible = q.visible;
              }
              if (typeof q.visible === 'function') {
                questionVisible = q.visible(b.answers, state.formQuestionnaire?.answers, state.preQualQuestionnaire?.answers);
              }
            }
            if (q.isValid !== true && questionVisible) {
              q.answer = '';
              validateQuestion(q);
              return { ...q, isForceEdited: true };
            }
            return { ...q };
          });
          blockValid = b.questions?.every(q => q.isValid === undefined || q.isValid === true) ?? true;
        }
        b = { ...b, isValid: blockValid, isForceEdited: true };
        return b;
      });

      updated = { ...updated, blocks: [...updatedBlocks] };

      return {
        ...state,
        preQualQuestionnaire: isPreQual ? updated : state.preQualQuestionnaire,
        formQuestionnaire: !isPreQual ? updated : state.formQuestionnaire,
      };
    }
    default:
      return state;
  }
}

export default questionnaires;
